import React from 'react';
import { connect } from 'react-redux';
import { Formik } from 'formik';
import Yup from 'yup';
import { Trans, withTranslation, WithTranslation } from 'react-i18next';
import Alert from '@jg/alert';
import { TARGET_MIN, TARGET_MAX, REQUEST } from '../../consts';
import {
  getRequestError,
  getRequestInFlight,
  getRequestSuccess,
} from '../../selectors/httpSelectors';
import {
  getTeamMembersTargetAmount,
  getCurrencyCode,
} from '../../selectors/apiSelectors';
import updateTeamMembersTarget from '../../actions/thunks/updateTeamMembersTarget';
import TeamMembersTargetForm from '../../components/TeamMembersTargetForm';
import { IAppState, IFundraisingConfiguration } from '../../types/state.d';
import { httpActions } from '../../actions';
import dispatchProps from '../../utils/dispatchProps';

const mapDispatchToProps = {
  updateTarget: updateTeamMembersTarget,
  resetRequestError: httpActions.resetRequestError,
};

type MapStateToProps = {
  isUpdateTeamMembersTargetInFlight?: boolean;
  teamMembersTarget: string | number;
  currencyCode: IFundraisingConfiguration['currencyCode'];
  isError: boolean;
  isSuccess: boolean;
};

type Props = WithTranslation &
  MappedDispatchProps<typeof mapDispatchToProps> &
  MapStateToProps;

export const ManageMembersTarget = ({
  t,
  teamMembersTarget,
  updateTarget,
  isUpdateTeamMembersTargetInFlight,
  currencyCode,
  isError,
  isSuccess,
  resetRequestError,
}: Props) => {
  const validationSchema = Yup.object().shape({
    teamMembersTarget: Yup.number()
      .min(TARGET_MIN, t('content.teamsettings.teammemberstargetmin'))
      .max(TARGET_MAX, t('content.teamsettings.teammemberstargetmax')),
  });
  return (
    <>
      <Formik
        initialValues={{
          teamMembersTarget,
        }}
        validationSchema={validationSchema}
        onSubmit={({ teamMembersTarget: target }) =>
          updateTarget(target).catch(() => {})
        }
        render={formikProps => (
          <TeamMembersTargetForm
            {...formikProps}
            currencyCode={currencyCode}
            isInFlight={isUpdateTeamMembersTargetInFlight}
            isSuccess={isSuccess}
          />
        )}
      />
      {isError && (
        <Alert type="error" displayType="inline" onClose={resetRequestError}>
          <Trans i18nKey="global.error.fatal" />
        </Alert>
      )}
    </>
  );
};

const mapStateToProps = (state: IAppState): MapStateToProps => ({
  isUpdateTeamMembersTargetInFlight: getRequestInFlight(
    REQUEST.UPDATE_TEAM_MEMBERS_TARGET,
  )(state),
  isError: getRequestError(REQUEST.UPDATE_TEAM_MEMBERS_TARGET)(state),
  isSuccess: getRequestSuccess(REQUEST.UPDATE_TEAM_MEMBERS_TARGET)(state),
  teamMembersTarget: getTeamMembersTargetAmount(state),
  currencyCode: getCurrencyCode(state),
});

export default connect(
  mapStateToProps,
  dispatchProps(mapDispatchToProps),
)(withTranslation()(ManageMembersTarget));
